import { useTranslation } from 'react-i18next';
import { GlobeIcon } from '@hero/web-icons';
import { useFinancingPartnersListApi } from '@hero/api-hooks';
import { resolvePilotLenderLogoUrl } from '../../utils/lenderLogo';

/**
 * Pilot lender display. Read-only — every carrier's application is
 * fanned out to all configured pilot partners so we can compare
 * offers across them and measure baseline conversion. The carrier
 * doesn't choose; the backend resolves the active lender list
 * server-side when no `lenderContactIds` are passed.
 *
 * Partners come from the static `financingPartners` config served
 * by `GET /financing/partners`. While the list is loading or empty
 * the fieldset shrinks to a single description line instead of
 * flashing skeletons — there are at most a handful of partners.
 *
 * Each card carries a `Visit website` link to the partner's site —
 * lets a carrier vet who'll see their application without leaving
 * the form (opens in a new tab).
 */
export function LendersFieldset() {
  const { t } = useTranslation('m-financing');
  const partnersQuery = useFinancingPartnersListApi({});
  const partners = partnersQuery.data?.partners ?? [];

  return (
    <fieldset className="space-y-3">
      <legend className="mb-1">
        <h3 className="text-base font-semibold text-gray-900">
          {t('apply_form.lenders.title')}
        </h3>
        <p className="mt-1 text-xs text-gray-500">
          {t('apply_form.lenders.description')}
        </p>
      </legend>

      {partners.length > 0 && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {partners.map(partner => {
            const initial = partner.name.trim().charAt(0).toUpperCase();
            const logoUrl = resolvePilotLenderLogoUrl(
              partner.slug,
              partner.logoUrl,
            );
            return (
              <div
                key={partner.id}
                className="flex items-center gap-3 rounded-2xl border border-emerald-200 bg-emerald-50/40 p-3">
                {logoUrl ? (
                  <img
                    src={logoUrl}
                    alt={partner.name}
                    className="size-12 shrink-0 rounded-xl object-contain"
                  />
                ) : (
                  <span
                    aria-hidden
                    className="flex size-12 shrink-0 items-center justify-center rounded-xl text-lg font-semibold text-white"
                    style={{ backgroundColor: partner.brandColor }}>
                    {initial}
                  </span>
                )}
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-gray-900">{partner.name}</div>
                  <div className="truncate text-xs text-gray-600">{partner.tagline}</div>
                </div>
                <a
                  href={partner.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex shrink-0 items-center gap-1 rounded-full bg-white px-2.5 py-1 text-[10px] font-semibold text-emerald-700 ring-1 ring-emerald-200 transition-colors hover:bg-emerald-50">
                  <GlobeIcon className="size-3" />
                  {t('apply_form.lenders.visit_website')}
                </a>
              </div>
            );
          })}
        </div>
      )}
    </fieldset>
  );
}
